import { z } from 'zod';

/**
 * What a consumer of models uses, chosen where it lives (`GET`/`PUT /api/models/consumers/:id`):
 * an agent's provider and model (`agent:<catalog id>`), Live's provider, voice and avatar
 * (`live`), dictation's provider (`dictation`). Without a choice, a consumer takes the user's
 * default model for what it makes (Settings › Models).
 */
export const agentModelChoiceSchema = z
  .object({
    /** A model provider plugin, or `own`: the agent's own account, no provider. */
    provider: z.string().min(1).max(256),
    model: z.string().min(1).max(256).optional(),
  })
  .strict();
/** A model and its provider (`inference.v1`); either may be left to the user's defaults. */
const modelChoiceSchema = z
  .object({
    provider: z.string().min(1).max(256).optional(),
    model: z.string().min(1).max(256).optional(),
  })
  .strict();
/**
 * How a voice consumer runs its calls: on a `realtime.v1` provider (`realtime`, the default: one
 * model that listens and speaks), or on the core's pipeline of `inference.v1` models (`pipeline`):
 * one listens live, one answers, one speaks.
 */
export const voiceModeSchema = z.enum(['realtime', 'pipeline']);
export const liveModelChoiceSchema = z
  .object({
    mode: voiceModeSchema.optional(),
    /** A `realtime.v1` provider plugin. */
    provider: z.string().min(1).max(256).optional(),
    /** The pipeline's listening (`realtime-transcription`), answering (`text`), speaking (`speech`). */
    listen: modelChoiceSchema.optional(),
    think: modelChoiceSchema.optional(),
    speak: modelChoiceSchema.optional(),
    /** A voice of `realtime.v1` calls, or of the pipeline's speech model. */
    voice: z.string().min(1).max(256).optional(),
    /** The plugin whose call view shows the agent (an avatar), or `none`; the first otherwise. */
    view: z.string().min(1).max(256).optional(),
  })
  .strict();
export const dictationModelChoiceSchema = z
  .object({
    mode: voiceModeSchema.optional(),
    /** A `realtime.v1` provider plugin. */
    provider: z.string().min(1).max(256).optional(),
    /** The pipeline's listening model (`realtime-transcription`). */
    listen: modelChoiceSchema.optional(),
  })
  .strict();

export type AgentModelChoice = z.infer<typeof agentModelChoiceSchema>;
export type LiveModelChoice = z.infer<typeof liveModelChoiceSchema>;
export type DictationModelChoice = z.infer<typeof dictationModelChoiceSchema>;
export type VoiceMode = z.infer<typeof voiceModeSchema>;
export type ModelConsumerChoice = AgentModelChoice | LiveModelChoice | DictationModelChoice;

/** An agent uses its own account rather than a provider of the user's. */
export const OWN_AGENT_ACCOUNT = 'own';
/** The consumers of voice calls (`realtime.v1`). */
export const VOICE_CONSUMERS = ['live', 'dictation'] as const;
export type VoiceConsumer = (typeof VOICE_CONSUMERS)[number];

/** A consumer's choice schema (`agent:<catalog id>`, `live`, `dictation`); null for no consumer. */
export function modelConsumerSchema(consumer: string): z.ZodType<ModelConsumerChoice> | null {
  if (/^agent:[a-z0-9][a-z0-9._-]{0,127}$/i.test(consumer)) return agentModelChoiceSchema;
  if (consumer === 'live') return liveModelChoiceSchema;
  if (consumer === 'dictation') return dictationModelChoiceSchema;
  return null;
}

/** `GET /api/models/consumers/:id`: the consumer's choice, null when the user chose nothing. */
export const modelConsumerResponseSchema = z.object({
  choice: z
    .union([agentModelChoiceSchema, liveModelChoiceSchema, dictationModelChoiceSchema])
    .nullable(),
});
export type ModelConsumerResponse = { choice: ModelConsumerChoice | null };
